// Ported from the reference Order app's
// manage-order-tabs/common/utility/merge-stops-and-status-updates.ts -
// builds the reverse-chronological timeline TrackingUpdatesDrawer renders,
// interleaving stop records with in-transit/info status updates around the
// first arrival event.
export function mergeStopsAndUpdates(
  stops: Record<number, any>,
  shipmentNumber: string,
  transportationNumber: string,
  transportId: string,
  carrierId: string,
  statusUpdates: any[]
) {
  if (!stops) return [];

  const filteredStops = Object.entries(stops).map(([key, stop]) => ({
    stopSequence: Number(key),
    transportationNumber,
    transportId,
    carrierId,
    type: 'stop',
    ...(stop as object)
  }));

  if (filteredStops.length === 0) return [];

  const sortedStops = filteredStops
    .slice()
    .sort((a, b) => a.stopSequence - b.stopSequence);

  if (!statusUpdates || statusUpdates.length === 0) {
    return sortedStops.slice().reverse();
  }

  const toTime = (u: any) => {
    const t = new Date(u?.updateReceivedOn).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const firstArrival = statusUpdates
    .filter(u => u.updateType === 2)
    .sort((a, b) => toTime(a) - toTime(b))[0];

  const arrivalTime = firstArrival
    ? toTime(firstArrival)
    : Number.MAX_SAFE_INTEGER;

  const prePickup = statusUpdates.filter(
    u => u.updateType === 1 && toTime(u) < arrivalTime
  );
  const inTransitAfterArrival = statusUpdates.filter(
    u => u.updateType === 1 && toTime(u) > arrivalTime
  );

  const infoBeforeArrival = statusUpdates.filter(
    u => u.updateType === 5 && toTime(u) < arrivalTime
  );
  const infoAfterArrival = statusUpdates.filter(
    u => u.updateType === 5 && toTime(u) > arrivalTime
  );

  const sortAsc = (arr: any[]) =>
    arr.slice().sort((a, b) => toTime(a) - toTime(b));

  const prePickupSorted = sortAsc(prePickup);
  const infoBeforeSorted = sortAsc(infoBeforeArrival);
  const inTransitAfterSorted = sortAsc(inTransitAfterArrival);
  const infoAfterSorted = sortAsc(infoAfterArrival);

  const combined: any[] = [];
  const makeId = () =>
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random()}`;

  const beforeFirst = sortAsc([...infoBeforeSorted, ...prePickupSorted]);
  beforeFirst.forEach(update => {
    combined.push({
      type: update.updateType === 5 ? 'info' : 'prePickup',
      id: makeId(),
      stopSequence: 0,
      transportationNumber,
      transportId,
      carrierId,
      isArrived: true,
      isDeparted: true,
      ...update
    });
  });

  const betweenStopsItems = sortAsc([
    ...inTransitAfterSorted,
    ...infoAfterSorted
  ]);

  for (let i = 0; i < sortedStops.length; i++) {
    const stop = sortedStops[i];

    combined.push({
      ...stop,
      type: 'stop'
    });

    if (i < sortedStops.length - 1) {
      betweenStopsItems.forEach(update => {
        combined.push({
          type: update.updateType === 1 ? 'inTransit' : 'info',
          id: makeId(),
          stopSequence: 0,
          transportationNumber,
          transportId,
          carrierId,
          isArrived: true,
          isDeparted: true,
          ...update
        });
      });
    }
  }

  return combined.reverse();
}

export default mergeStopsAndUpdates;
